import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
import NumberInputField from 'components/inputFields/NumberInputField';
// Models
import mParkingCategory, { isOnDemand } from 'models/cpm/parkingCategory';
import mPlanning from 'models/cpm/planning';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Custom
import { cpmParkingCategoryDataShape, errorShape, cpmPlanningDataShape } from 'constants/PropTypesShapes';
import { buildFormErrors, cpmParkingCategoryConstraints } from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import ColorPicker from 'components/inputFields/ColorPicker';
import FormHelper from 'components/misc/FormHelper';
import { sanitizeInt } from 'utils/Tools';
import ResourcesSelectFieldWithCreateButton from 'components/inputFields/ResourcesSelectFieldWithCreateButton';
import PlanningInfoButton from 'cpmCarparks/assets/plannings/PlanningInfoButton';

const DEFAULT_COLOR = '#757575';

class ParkingCategoryAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);

    // Used to store the id of an added planning
    this.addedPlanningId = null;
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);

    // Default color
    const { fields: { color }, usedColors, onFieldsChange } = this.props;
    if (color === DEFAULT_COLOR) {
      // We are here only in case of adding a parking category
      // Choose in the predefined colors the first that is not used
      const placeholderColor = _find(mParkingCategory.predefinedColors, (c) => !_includes(usedColors, c));
      if (placeholderColor) {
        onFieldsChange({ color: placeholderColor });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      planningAdd, planningsLoading,
      onFieldsChange,
    } = this.props;
    const {
      planningAdd: prevPlanningAdd, planningsLoading: prevPlanningsLoading,
    } = prevProps;

    // Catch if a new planning was added
    if (planningAdd.added && !prevPlanningAdd.added) {
      this.addedPlanningId = planningAdd.result.id;
    }
    // Catch end of plannings reload to set the added planning
    if (!planningsLoading && prevPlanningsLoading) {
      onFieldsChange({
        defaultAllotmentPlanningId: this.addedPlanningId,
      });
    }
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        name, color,
        defaultAllotmentAssignedCapacitiesMax, defaultAllotmentAssignedCapacitiesMin,
        defaultAllotmentBookingCapacitiesMax, defaultAllotmentBookingCapacitiesMin,
        defaultAllotmentWithoutBookingCapacitiesMax, defaultAllotmentWithoutBookingCapacitiesMin,
        defaultAllotmentPlanningId,
      },
      parkingCategory, onErrorsChange, onValidate,
    } = this.props;

    const defaultAllotment = {
      assignedCapacities: {
        max: defaultAllotmentAssignedCapacitiesMax,
        min: defaultAllotmentAssignedCapacitiesMin,
      },
      bookingCapacities: {
        max: defaultAllotmentBookingCapacitiesMax,
        min: defaultAllotmentBookingCapacitiesMin,
      },
      withoutBookingCapacities: {
        max: defaultAllotmentWithoutBookingCapacitiesMax,
        min: defaultAllotmentWithoutBookingCapacitiesMin,
      },
      planningId: defaultAllotmentPlanningId,
    };

    const errors = buildFormErrors({ name, color }, cpmParkingCategoryConstraints);
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(mParkingCategory.serialize({
        name, color, defaultAllotment,
      }), parkingCategory);
      return true;
    }
    return false;
  }

  render() {
    const {
      fields: {
        name, color,
        defaultAllotmentAssignedCapacitiesMax,
        defaultAllotmentBookingCapacitiesMax, defaultAllotmentBookingCapacitiesMin,
        defaultAllotmentWithoutBookingCapacitiesMax, defaultAllotmentWithoutBookingCapacitiesMin,
        defaultAllotmentPlanningId,
      }, errors, onAddPlanning, onFieldsChange, plannings, planningsLoading, parkingCategory,
    } = this.props;

    // Permissions
    const { pCpm: { planningRead } } = permissions;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          {/* Section Name & Color */}
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: (
                      <LocalizedMessage id="help.parkingCategories.formColor" />
                    ),
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-lg-8">
                      {!isOnDemand(parkingCategory)
                        && (
                        <TextField
                          required
                          label={<LocalizedMessage id="field.name" />}
                          errorText={_get(errors, 'name')}
                          value={name}
                          onChange={(value) => onFieldsChange({ name: value })}
                        />
                        )}
                    </div>
                    <div className="col-12 col-lg-8">
                      <ColorPicker
                        errorText={_get(errors, 'color')}
                        value={color}
                        onChange={(value) => onFieldsChange({ color: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
          {/* Section Configuration */}
          <div className="col-12 pt-3">
            <div className="d-flex">
              <FormHelper
                elements={!isOnDemand(parkingCategory)
                  ? [
                    {
                      title: <LocalizedMessage id="field.parkingCategoryMaximumBooking" />,
                      content: <LocalizedMessage id="help.parkingCategories.maximumNumberOfBookings" />,
                    },
                    {
                      title: <LocalizedMessage id="field.parkingCategoryGuaranteedBooking" />,
                      content: <LocalizedMessage id="help.parkingCategories.numberOfGuaranteedBookings" />,
                    },
                    {
                      title: <LocalizedMessage id="field.parkingCategorySpacesAssigned" />,
                      content: <LocalizedMessage id="help.parkingCategories.numberOfSpacesAssignedToUsers" />,
                    },
                    {
                      title: <LocalizedMessage id="field.parkingCategoryMaximumWithoutBooking" />,
                      content: <LocalizedMessage id="help.parkingCategories.maximumNumberOfAccessesWithoutBookings" />,
                    },
                    {
                      title: <LocalizedMessage id="field.parkingCategoryGuaranteedWithoutBooking" />,
                      content: <LocalizedMessage id="help.parkingCategories.numberOfGuaranteedSpacesWithoutBookings" />,
                    },
                  ]
                  : [
                    {
                      title: <LocalizedMessage id="field.parkingCategoryGuaranteedWithoutBooking" />,
                      content: <LocalizedMessage id="help.parkingCategories.numberOfGuaranteedSpacesWithoutBookings" />,
                    },
                  ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="title.configuration" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    {!isOnDemand(parkingCategory) && (
                    <React.Fragment>
                      <div
                        className="d-flex col-12 col-lg-8"
                        style={{ alignItems: 'center' }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: '70%',
                            backgroundColor:
                                  mParkingCategory.configuration.bookingMax.color,
                            marginRight: 10,
                          }}
                        />
                        <NumberInputField
                          required
                          sanitizer={sanitizeInt}
                          inputMaxWidth={70}
                          label={(
                            <LocalizedMessage
                              id={
                                    mParkingCategory.configuration.bookingMax.labelId
                                  }
                            />
                              )}
                          errorText={_get(
                            errors,
                            'defaultAllotment.bookingCapacities.max',
                          )}
                          value={defaultAllotmentBookingCapacitiesMax}
                          onChange={(value) => onFieldsChange({ defaultAllotmentBookingCapacitiesMax: value })}
                        />
                      </div>
                      <div
                        className="d-flex col-12 col-lg-8"
                        style={{ alignItems: 'center' }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: '70%',
                            backgroundColor:
                                  mParkingCategory.configuration.bookingMin.color,
                            marginRight: 10,
                          }}
                        />
                        <NumberInputField
                          required
                          sanitizer={sanitizeInt}
                          inputMaxWidth={70}
                          label={(
                            <LocalizedMessage
                              id={
                                    mParkingCategory.configuration.bookingMin.labelId
                                  }
                            />
                              )}
                          errorText={_get(
                            errors,
                            'defaultAllotment.bookingCapacities.min',
                          )}
                          value={defaultAllotmentBookingCapacitiesMin}
                          onChange={(value) => onFieldsChange({ defaultAllotmentBookingCapacitiesMin: value })}
                        />
                      </div>
                      <div
                        className="d-flex col-12 col-lg-8"
                        style={{ alignItems: 'center' }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: '70%',
                            backgroundColor:
                                  mParkingCategory.configuration.assigned.color,
                            marginRight: 10,
                          }}
                        />
                        <NumberInputField
                          required
                          sanitizer={sanitizeInt}
                          inputMaxWidth={70}
                          label={(
                            <LocalizedMessage
                              id={
                                    mParkingCategory.configuration.assigned.labelId
                                  }
                            />
                              )}
                          errorText={_get(
                            errors,
                            'defaultAllotment.assignedCapacities.max',
                          ) || _get(
                            errors,
                            'defaultAllotment.assignedCapacities.min',
                          )}
                          value={defaultAllotmentAssignedCapacitiesMax}
                          onChange={(value) => onFieldsChange({ defaultAllotmentAssignedCapacitiesMax: value, defaultAllotmentAssignedCapacitiesMin: value })}
                        />
                      </div>
                      <div
                        className="d-flex col-12 col-lg-8"
                        style={{ alignItems: 'center' }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: '70%',
                            backgroundColor:
                                  mParkingCategory.configuration.withoutBookingMax.color,
                            marginRight: 10,
                          }}
                        />
                        <NumberInputField
                          required
                          sanitizer={sanitizeInt}
                          inputMaxWidth={70}
                          label={(
                            <LocalizedMessage
                              id={
                                    mParkingCategory.configuration.withoutBookingMax.labelId
                                  }
                            />
                              )}
                          errorText={_get(
                            errors,
                            'defaultAllotment.withoutBookingCapacities.max',
                          )}
                          value={defaultAllotmentWithoutBookingCapacitiesMax}
                          onChange={(value) => onFieldsChange({ defaultAllotmentWithoutBookingCapacitiesMax: value })}
                        />
                      </div>
                    </React.Fragment>
                    )}

                    <div
                      className="d-flex col-12 col-lg-8"
                      style={{ alignItems: 'center' }}
                    >
                      <div
                        style={{
                          width: 5,
                          height: '70%',
                          backgroundColor:
                              mParkingCategory.configuration.withoutBookingMin.color,
                          marginRight: 10,
                        }}
                      />
                      <NumberInputField
                        required
                        sanitizer={sanitizeInt}
                        inputMaxWidth={70}
                        label={(
                          <LocalizedMessage
                            id={
                                mParkingCategory.configuration.withoutBookingMin.labelId
                              }
                          />
                          )}
                        errorText={_get(
                          errors,
                          'defaultAllotment.withoutBookingCapacities.min',
                        )}
                        value={defaultAllotmentWithoutBookingCapacitiesMin}
                        onChange={(value) => onFieldsChange({ defaultAllotmentWithoutBookingCapacitiesMin: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
          {/* Section Planning Restriction */}
          <OnlyWith
            permissions={{
              needed: planningRead,
              permissionsOrContext: 'carparkPermissions',
            }}
          >
            <div className="col-12 pt-3">
              <div className="d-flex">
                <FormHelper elements={[]} />
                <div style={{ flex: 1 }}>
                  <Card
                    title={(
                      <Typography type="subtitle2">
                        <LocalizedMessage id="title.planningRestriction" />
                      </Typography>
                    )}
                  >
                    <ResourcesSelectFieldWithCreateButton
                      loading={planningsLoading}
                      infoButton={
                        !planningsLoading && defaultAllotmentPlanningId ? (
                          <div className="ml-1">
                            <PlanningInfoButton planningId={defaultAllotmentPlanningId} />
                          </div>
                        ) : null
                      }
                      selectFieldProps={{
                        labelId: 'field.planning',
                        value: defaultAllotmentPlanningId,
                        resources: mPlanning.sortElements(plannings),
                        withNullItem: true,
                        displayKey: 'name',
                        onChange: (value) => onFieldsChange({ defaultAllotmentPlanningId: value }),
                      }}
                      onAddResource={onAddPlanning}
                    />
                  </Card>
                </div>
              </div>
            </div>
          </OnlyWith>
        </div>
      </FormDialogTemplate>
    );
  }
}

ParkingCategoryAddEditForm.propTypes = {
  operation: PropTypes.oneOf(['add', 'update']).isRequired, // eslint-disable-line react/no-unused-prop-types
  usedColors: PropTypes.arrayOf(PropTypes.string),
  parkingCategory: cpmParkingCategoryDataShape,
  plannings: PropTypes.arrayOf(cpmPlanningDataShape),
  planningsLoading: PropTypes.bool.isRequired,
  planningAdd: PropTypes.shape({
    added: PropTypes.bool.isRequired,
    result: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onAddPlanning: PropTypes.func,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

ParkingCategoryAddEditForm.defaultProps = {
  errors: null,
  onAddPlanning: null,
  parkingCategory: null,
  plannings: null,
  operationError: null,
  usedColors: null,
};

const mapFieldsToProps = {
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  color: {
    resourceKey: 'color',
    defaultValue: DEFAULT_COLOR,
  },
  defaultAllotmentAssignedCapacitiesMax: {
    resourceKey: 'defaultAllotment.assignedCapacities.max',
    defaultValue: 0,
  },
  defaultAllotmentAssignedCapacitiesMin: {
    resourceKey: 'defaultAllotment.assignedCapacities.min',
    defaultValue: 0,
  },
  defaultAllotmentBookingCapacitiesMax: {
    resourceKey: 'defaultAllotment.bookingCapacities.max',
    defaultValue: 0,
  },
  defaultAllotmentBookingCapacitiesMin: {
    resourceKey: 'defaultAllotment.bookingCapacities.min',
    defaultValue: 0,
  },
  defaultAllotmentWithoutBookingCapacitiesMax: {
    resourceKey: 'defaultAllotment.withoutBookingCapacities.max',
    defaultValue: 0,
  },
  defaultAllotmentWithoutBookingCapacitiesMin: {
    resourceKey: 'defaultAllotment.withoutBookingCapacities.min',
    defaultValue: 0,
  },
  defaultAllotmentPlanningId: {
    resourceKey: 'defaultAllotment.planning',
    defaultValue: null,
    setTransform: (planning) => _get(planning, 'id'),
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.parkingCategory,
  operationError: ownProps.operationError,
});

const mapStateToProps = (state, ownProps) => ({
  operationError: _get(state.cpmCarparkParkingCategory, [ownProps.operation, 'error']),
  plannings: state.cpmCarparkPlanning.list.elements,
  planningsLoading: state.cpmCarparkPlanning.list.loading,
  planningAdd: state.cpmCarparkPlanning.add,
});

module.exports = compose(
  connect(
    mapStateToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['addCarparkParkingCategory', 'updateCarparkParkingCategory']),
)(ParkingCategoryAddEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/parkingCategories/ParkingCategoryAddEditForm.js